import { useState } from 'react';
import { api, type Adapter } from '../lib/api.ts';
import { AdapterForm } from './AdapterForm.tsx';
import { BluetoothTools, transportLabel, WifiTools } from './AdapterTools.tsx';
import { Modal } from './Modal.tsx';

interface Props {
  adapters: Adapter[];
  selected: Adapter | null;
  canWrite: boolean;
  onSelect: (adapter: Adapter) => void;
  onAdaptersChanged: () => void;
  onClose: () => void;
}

/**
 * Everything about adapters in one place.
 *
 * This used to be spread down the sidebar: a picker at the top, Wi-Fi controls
 * in the middle and a "Saved adapters" editor at the bottom, three sections for
 * one thing that most visits never touch.
 */
export function AdapterSettings({ adapters, selected, canWrite, onSelect, onAdaptersChanged, onClose }: Props) {
  const [mode, setMode] = useState<{ kind: 'list' } | { kind: 'add' } | { kind: 'edit'; adapter: Adapter }>({
    kind: 'list',
  });
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(a: Adapter) {
    setError(null);
    try {
      await api.deleteAdapter(a.id);
      setConfirmDelete(null);
      onAdaptersChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (mode.kind !== 'list') {
    return (
      <Modal title={mode.kind === 'add' ? 'Add an adapter' : `Edit ${mode.adapter.name}`} onClose={onClose}>
        <AdapterForm
          initial={mode.kind === 'edit' ? mode.adapter : undefined}
          onCancel={() => setMode({ kind: 'list' })}
          onSaved={(adapter) => {
            onAdaptersChanged();
            // Someone adding their first adapter here wants to use it.
            if (mode.kind === 'add' && !selected) onSelect(adapter);
            setMode({ kind: 'list' });
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal title="Adapter settings" onClose={onClose}>
      <div className="stack">
        {selected && (
          <section className="stack">
            <span className="eyebrow" style={{ margin: 0 }}>
              You scan with
            </span>
            <div className="adapter-current">
              <strong>{selected.name}</strong>
              <span className="small dim">{transportLabel(selected.transport)}</span>
            </div>
            {selected.transport === 'wifi' && <WifiTools adapter={selected} onAdapterChanged={onAdaptersChanged} />}
            {selected.transport === 'ble' && <BluetoothTools />}
          </section>
        )}

        <section className="stack" style={{ gap: 6 }}>
          <div className="row">
            <span className="eyebrow" style={{ margin: 0, flex: 1 }}>
              Your company's adapters
            </span>
            {canWrite && (
              <button className="tiny" onClick={() => setMode({ kind: 'add' })}>
                Add adapter
              </button>
            )}
          </div>

          {adapters.length === 0 && <span className="small dim">None yet.</span>}

          {adapters.map((a) => (
            <div key={a.id} className="adapter-row">
              <div className="adapter-row-main">
                <span className="adapter-row-name">
                  {a.name}
                  {a.hasWifiKey && <span className="pill ok tiny-pill">key stored</span>}
                </span>
                <span className="small dim">{transportLabel(a.transport)}</span>
              </div>
              <div className="row">
                {a.id === selected?.id ? (
                  <span className="pill info tiny-pill">in use</span>
                ) : (
                  <button className="tiny" onClick={() => onSelect(a)}>
                    Use
                  </button>
                )}
                {canWrite && (
                  <>
                    <button className="tiny ghost" onClick={() => setMode({ kind: 'edit', adapter: a })}>
                      edit
                    </button>
                    <button
                      className="tiny ghost"
                      onClick={() => setConfirmDelete(confirmDelete === a.id ? null : a.id)}
                    >
                      delete
                    </button>
                  </>
                )}
              </div>
              {/* On the row rather than in a browser dialog, so the name being
                  deleted stays in view while the question is read. */}
              {confirmDelete === a.id && (
                <div className="person-confirm">
                  <p className="small">
                    Delete <strong>{a.name}</strong> for everyone at your company? Anyone scanning with it will be asked
                    to choose another. Past scans keep their record.
                  </p>
                  <div className="row">
                    <button className="tiny danger" onClick={() => remove(a)}>
                      delete adapter
                    </button>
                    <button className="tiny ghost" onClick={() => setConfirmDelete(null)}>
                      cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>

        {error && <div className="banner error small">{error}</div>}
      </div>
    </Modal>
  );
}
